import React, { useState } from "react";
import useSWR from "swr";
import { CheckCircle2, Clock, Circle, Camera, Calendar, X } from "lucide-react";
import { api, fmtDate, relativeTime } from "@/lib/api";
import { Skeleton, ProgressRing } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

const MILESTONE_IMG = (i) => {
  // Construction-relevant imagery only — ordered to match milestone progression
  const pool = [
    "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80", // excavation
    "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=800&q=80", // foundation
    "https://images.unsplash.com/photo-1503328427499-d92d1ac3d174?auto=format&fit=crop&w=800&q=80", // structure / crane
    "https://images.unsplash.com/photo-1590074072786-a66914d668f1?auto=format&fit=crop&w=800&q=80", // upper floors construction
    "https://images.unsplash.com/photo-1604709177595-ee9c2580e9a2?auto=format&fit=crop&w=800&q=80", // brickwork
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=800&q=80", // electrical
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80", // interior flooring
    "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80", // painting / finishing
  ];
  return pool[i % pool.length];
};

export default function BuyerProgress() {
  const { data } = useSWR("/me/progress", fetcher);
  const pct = data?.completion_pct ?? 0;
  const [zoom, setZoom] = useState(null); // {photo, milestone}

  return (
    <div className="space-y-5">
      <div>
        <div className="rc-eyebrow">Construction</div>
        <div className="rc-title-1 mt-1">My Home Progress</div>
        <div className="rc-callout">Real-time milestones, updated by the on-site engineering team.</div>
      </div>

      <div className="rc-card rc-card-padded flex flex-col md:flex-row items-center gap-6 rc-rise">
        <ProgressRing value={pct} size={180}/>
        <div className="flex-1 min-w-0">
          <div className="rc-title-2">Overall Completion</div>
          <div className="rc-callout mt-1">Site engineers update this view as work progresses, with photos from the construction site.</div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div><div className="rc-caption uppercase">Completed</div><div className="rc-headline rc-tabular" style={{ color: "var(--rc-success)" }}>{data?.milestones?.filter(m => m.status === "completed").length || 0}</div></div>
            <div><div className="rc-caption uppercase">In progress</div><div className="rc-headline rc-tabular" style={{ color: "var(--rc-orange)" }}>{data?.milestones?.filter(m => m.status === "in_progress").length || 0}</div></div>
            <div><div className="rc-caption uppercase">Upcoming</div><div className="rc-headline rc-tabular">{data?.milestones?.filter(m => m.status === "pending").length || 0}</div></div>
          </div>
        </div>
      </div>

      <div className="rc-card rc-card-padded">
        <div className="rc-title-3 mb-4">Milestones</div>
        {!data && Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={120} className="mb-3"/>)}
        <div className="relative pl-8">
          <div className="absolute left-[14px] top-2 bottom-2 w-[2px] bg-[var(--rc-line)]"/>
          {data?.milestones?.map((m, i) => {
            const done = m.status === "completed";
            const inprog = m.status === "in_progress";
            return (
              <div key={m.milestone_id} className="relative pb-5 rc-rise" style={{ animationDelay: `${i * 40}ms` }} data-testid={`milestone-${i}`}>
                <div className="absolute -left-[22px] top-2 bg-white rounded-full p-0.5 z-10">
                  {done ? <CheckCircle2 className="text-[var(--rc-orange)] block" size={22} strokeWidth={2}/> :
                   inprog ? <Clock className="text-[var(--rc-orange)] block" size={22}/> :
                   <Circle className="text-[var(--rc-grey-icon)] block" size={22}/>}
                </div>
                <div className="rc-card overflow-hidden" style={{ padding: 0 }}>
                  {(done || inprog) && (
                    <button
                      type="button"
                      onClick={() => setZoom({ photo: MILESTONE_IMG(i), milestone: m })}
                      className="relative h-32 sm:h-40 w-full block group cursor-zoom-in"
                      data-testid={`milestone-photo-${i}`}
                    >
                      <div className="absolute inset-0 transition-transform group-hover:scale-105" style={{ backgroundImage: `url(${MILESTONE_IMG(i)})`, backgroundSize: "cover", backgroundPosition: "center" }}/>
                      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.75) 100%)" }}/>
                      <div className="absolute bottom-2.5 left-3 right-3 flex items-center justify-between text-white rc-footnote" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}>
                        <span className="flex items-center gap-1"><Camera size={12}/> Site photo · tap to view</span>
                        <span className="opacity-90">{relativeTime(m.updated_at)}</span>
                      </div>
                    </button>
                  )}
                  <div className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="rc-headline" style={{ flex: "1 1 60%", minWidth: 0 }}>{m.milestone_name}</div>
                      <div className="rc-headline rc-tabular" style={{ color: done ? "var(--rc-success)" : inprog ? "var(--rc-orange)" : "var(--rc-label-secondary)" }}>{m.completion_pct}%</div>
                    </div>
                    <div className="rc-footnote mt-1 flex items-center gap-1 flex-wrap">
                      <Calendar size={12}/>
                      <span>Planned {fmtDate(m.planned_date)}{m.actual_date ? ` · Actual ${fmtDate(m.actual_date)}` : ""}</span>
                    </div>
                    <div className="mt-3 h-[6px] bg-[var(--rc-near-white)] rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: `${m.completion_pct}%`, background: "linear-gradient(90deg, #F9A61C, #F27024)", transition: "width 800ms ease" }}/>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Photo zoom modal */}
      {zoom && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 rc-fade-in"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          onClick={() => setZoom(null)}
          data-testid="photo-modal"
        >
          <button
            onClick={() => setZoom(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
            data-testid="close-modal"
            aria-label="Close"
          >
            <X size={20}/>
          </button>
          <div className="max-w-3xl w-full max-h-[85vh] rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img src={zoom.photo} alt={zoom.milestone.milestone_name} className="w-full h-auto max-h-[70vh] object-contain bg-black"/>
            <div className="p-4 text-white" style={{ background: "rgba(0,0,0,0.65)" }}>
              <div className="rc-headline">{zoom.milestone.milestone_name}</div>
              <div className="rc-footnote text-white/75 mt-1">
                {zoom.milestone.completion_pct}% complete · Planned {fmtDate(zoom.milestone.planned_date)}
                {zoom.milestone.actual_date ? ` · Actual ${fmtDate(zoom.milestone.actual_date)}` : ""}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
